import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, filter, map, switchMap, timer } from "rxjs";
import { DigitalEquipment, Roadwork } from "src/app/models";

import { ROADWORK_INTERVAL } from "../config/app.config";
import { DigitalRoadworkDataService } from "./digital-roadwork-data.service";

@Injectable({
	providedIn: "root",
	deps: [DigitalRoadworkDataService],
})
export class DigitalRoadworkService {
	private readonly roadworks: BehaviorSubject<Roadwork[] | null> = new BehaviorSubject<Roadwork[] | null>(null);
	private readonly nearestRoadworkIds: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
	private readonly digitalEquipmentFromUser: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	private readonly allDigitalEquipments: BehaviorSubject<Map<string, DigitalEquipment>> = new BehaviorSubject<
		Map<string, DigitalEquipment>
	>(new Map<string, DigitalEquipment>());

	public currentRoadworks: Observable<Roadwork[] | null> = this.roadworks.asObservable().pipe(filter((roadworks) => !!roadworks));
	public currentNearestRoadworkIds: Observable<string[] | null> = this.nearestRoadworkIds.asObservable();
	public idOfCurrentDigitalEquipmentFromUser: Observable<string | null> = this.digitalEquipmentFromUser.asObservable();
	public currentAllDigitalEquipments: Observable<Map<string, DigitalEquipment>> = this.allDigitalEquipments.asObservable();

	public constructor(private readonly digitalRoadworkDataService: DigitalRoadworkDataService) {}

	public initialize() {
		timer(0, ROADWORK_INTERVAL)
			.pipe(switchMap(() => this.digitalRoadworkDataService.getRoadworks()))
			.subscribe((roadworks) => {
				this.roadworks.next(roadworks);
			});

		timer(0, ROADWORK_INTERVAL)
			.pipe(switchMap(() => this.digitalRoadworkDataService.getAllDigitalEquipments()))
			.subscribe((digitalEquipments) => {
				this.allDigitalEquipments.next(new Map(digitalEquipments.map((equipment) => [equipment.assetId, equipment])));
			});
	}

	public getRoadworkById(id: string): Observable<Roadwork | undefined> {
		return this.currentRoadworks.pipe(
			filter((roadworks) => !!roadworks),
			map((roadworks) => (roadworks as Roadwork[]).find((roadwork) => roadwork.id === id)),
		);
	}

	public setNearestRoadworks(roadworks: Roadwork[]) {
		this.nearestRoadworkIds.next(roadworks.map((r) => r.id));
	}

	public setDigitalEquipmentFromUser(digitalEquipmentAssetId: string | null) {
		this.digitalEquipmentFromUser.next(digitalEquipmentAssetId);
	}

	public updateRoadworks() {
		this.digitalRoadworkDataService.getRoadworks().subscribe((roadworks) => {
			this.roadworks.next(roadworks);
		});
	}
}
